import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { 
  searchUsers, 
  sendFriendRequest, 
  acceptFriendRequest, 
  rejectFriendRequest, 
  removeFriend, 
  getFriends, 
  getFriendRequests, 
  getSentFriendRequests,
  cancelFriendRequest,
  SocialUser,
  getUserProfileStats
} from '../services/socialService';
import { getActiveChallenges, getCompletedChallenges, createChallenge, getChallengeById, ChallengeData, ChallengeType } from '../services/challengeService';
import { sendProvocation } from '../services/notificationService';
import { validateGrammar, ValidationResult } from '../services/grammarValidationService';
import { Search, Users, Swords, UserPlus, UserMinus, Flame, AlertCircle, Share2, Download, Trophy, Target, UserCheck, Clock, MessageSquare, CheckCircle, XCircle, X, Frown, BookOpen, Sparkles, StopCircle } from 'lucide-react';
import html2canvas from 'html2canvas';
import { getAvatarUrl } from '../utils/avatarUtils';
import ChallengeSummaryList from '../components/ChallengeSummaryList';
import ChallengeShareCard from '../components/social/ChallengeShareCard';
import ProvokeShareCard from '../components/social/ProvokeShareCard';
import FriendsWithoutStudyShareCard from '../components/social/FriendsWithoutStudyShareCard';
import {
  createFriendSession,
  listenForPendingSessionInvites,
  acceptFriendSession,
  declineFriendSession,
} from '../services/friendSessionService';
import { createStopGameWithFriend } from '../services/stopGameRoomService';

export default function SocialPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const urlChallengeId = searchParams.get('challengeId');
  const urlFriendId = searchParams.get('id');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<(SocialUser & { friendStatus: string })[]>([]);
  const [friends, setFriends] = useState<SocialUser[]>([]);
  const [friendRequests, setFriendRequests] = useState<SocialUser[]>([]);
  const [sentFriendRequests, setSentFriendRequests] = useState<SocialUser[]>([]);
  const [activeChallenges, setActiveChallenges] = useState<ChallengeData[]>([]);
  const [completedChallenges, setCompletedChallenges] = useState<ChallengeData[]>([]);
  const [sharingChallenge, setSharingChallenge] = useState<ChallengeData | null>(null);
  
  const [activeTab, setActiveTab] = useState<'friends' | 'requests' | 'challenges' | 'search'>(
    (urlTab as any) || 'friends'
  );

  useEffect(() => {
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab as any);
    }
  }, [urlTab]);

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setSearchParams(prev => {
      prev.set('tab', tab);
      return prev;
    });
  };
  const [isSearching, setIsSearching] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [loadingActionId, setLoadingActionId] = useState<string | null>(null);
  
  const [challengeModalUserId, setChallengeModalUserId] = useState<string | null>(null);
  
  const [provokeModalUserId, setProvokeModalUserId] = useState<string | null>(null);
  const [selectedFriendStats, setSelectedFriendStats] = useState<any>(null);
  const [provokeMessage, setProvokeMessage] = useState('');
  const [isTypingProvoke, setIsTypingProvoke] = useState(false);
  const [provokeValidation, setProvokeValidation] = useState<ValidationResult | null>(null);
  const [isCheckingProvoke, setIsCheckingProvoke] = useState(false);
  const [isProcessingChallenge, setIsProcessingChallenge] = useState<string | null>(null);
  const [challengeSuccess, setChallengeSuccess] = useState<string | null>(null);
  const [provokeSuccess, setProvokeSuccess] = useState<boolean>(false);
  const [provokeContext, setProvokeContext] = useState<string>('');

  const [viewingChallenge, setViewingChallenge] = useState<ChallengeData | null>(null);
  const [loadingChallenge, setLoadingChallenge] = useState(false);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [isBulkProvoke, setIsBulkProvoke] = useState(false);
  const friendsWithoutStudyShareRef = useRef<HTMLDivElement>(null);
  
  const friendsWithoutStudy = React.useMemo(() => {
    return friends.filter(user => {
      const days = user.daysWithoutStudy === undefined ? 0 : user.daysWithoutStudy;
      const streak = user.streak || 0;
      return days > 1 || (days === 1 && streak === 0);
    });
  }, [friends]);

  const currentProvokeOptions = React.useMemo(() => {
    const options = [
      "Hey lazybones, time to study! 📚",
      "I'm passing you on the leaderboard! 🏃‍♂️",
      "Your streak is crying! 😭",
      "Don't give up now! 💪",
      "Where did you go? We miss you! 🥺"
    ];

    if (!selectedFriendStats) return options;

    const { stats, daysWithoutStudy, user } = selectedFriendStats;
    const dynamicOptions: string[] = [];

    if (stats?.crimeGamesSolved > 0) {
      dynamicOptions.push(`Hey Sherlock Holmes, why aren't you solving crimes? 🕵️‍♂️`);
    }

    if (daysWithoutStudy > 1 || (daysWithoutStudy === 1 && (!user?.streak || user.streak === 0))) {
      dynamicOptions.push(`${daysWithoutStudy} ${daysWithoutStudy === 1 ? 'day' : 'days'} without studying, this is sad! 😢`);
    } else if (daysWithoutStudy === -1) {
      dynamicOptions.push(`You haven't started studying yet! Let's go! 🚀`);
    }

    if (user?.streak > 0) {
      dynamicOptions.push(`Don't lose your ${user.streak} day streak! 🔥`);
    }

    if (stats?.dailyTrainingDone > 0) {
      dynamicOptions.push(`I bet you can't beat your daily training record! 🎯`);
    }

    return [...dynamicOptions, ...options];
  }, [selectedFriendStats]);
  
  const shareCardRef = useRef<HTMLDivElement>(null);
  const provokeShareRef = useRef<HTMLDivElement>(null);

  const [pendingInvites, setPendingInvites] = useState<Array<{ sessionId: string; hostId: string; hostName: string; hostPhoto?: string }>>([]);

  useEffect(() => {
    if (!currentUser) return;
    
    const unsubFriends = getFriends(currentUser.uid, setFriends);
    const unsubReqs = getFriendRequests(currentUser.uid, setFriendRequests);
    const unsubSentReqs = getSentFriendRequests(currentUser.uid, setSentFriendRequests);
    const unsubActiveChallenges = getActiveChallenges(currentUser.uid, setActiveChallenges);
    const unsubCompletedChallenges = getCompletedChallenges(currentUser.uid, setCompletedChallenges);
    const unsubSessionInvites = listenForPendingSessionInvites(currentUser.uid, setPendingInvites);
    
    return () => {
      unsubFriends();
      unsubReqs();
      unsubSentReqs();
      unsubActiveChallenges();
      unsubCompletedChallenges();
      unsubSessionInvites();
    };
  }, [currentUser]);

  const handleStartSession = async (friendUser: SocialUser) => {
    if (!currentUser) return;
    try {
      setLoadingActionId(`session_${friendUser.id}`);
      const sessionId = await createFriendSession(
        {
          id: currentUser.uid,
          name: currentUser.displayName || currentUser.email?.split('@')[0] || 'Learner',
          photo: currentUser.photoURL || undefined,
        },
        {
          id: friendUser.id,
          name: friendUser.username || 'Friend',
          photo: friendUser.profilePhotoUrl || undefined,
        }
      );
      navigate(`/session/${sessionId}`);
    } catch (err) {
      console.error('Failed to create friend session:', err);
      setToastMessage('Failed to start study session. Please try again.');
    } finally {
      setLoadingActionId(null);
    }
  };

  const handleStartStopGame = async (friendUser: SocialUser) => {
    if (!currentUser) return;
    try {
      setLoadingActionId(`stop_${friendUser.id}`);
      const hostName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Learner';
      const roomCode = await createStopGameWithFriend(
        {
          id: currentUser.uid,
          name: hostName,
          avatarUrl: currentUser.photoURL || undefined
        },
        {
          id: friendUser.id,
          name: friendUser.username || 'Friend',
          avatarUrl: friendUser.profilePhotoUrl || undefined
        }
      );
      navigate(`/stop-game?room=${roomCode}`);
    } catch (err) {
      console.error('Failed to start STOP game with friend:', err);
      setToastMessage('Failed to start STOP game. Please try again.');
    } finally {
      setLoadingActionId(null);
    }
  };

  useEffect(() => {
    if (!currentUser) return;
    
    if (searchQuery.length >= 2) {
      setIsSearching(true);
      const unsubSearch = searchUsers(searchQuery, currentUser.uid, (results) => {
        setSearchResults(results);
        setIsSearching(false);
      });
      return () => unsubSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, currentUser]);

  useEffect(() => {
    const fetchChallenge = async () => {
      if (urlChallengeId && currentUser) {
        setLoadingChallenge(true);
        try {
          const challenge = await getChallengeById(urlChallengeId);
          if (challenge) {
            setViewingChallenge(challenge);
            if (activeTab !== 'challenges') {
              handleTabChange('challenges');
            }
          }
        } catch (e) {
          console.error('Failed to fetch challenge', e);
        } finally {
          setLoadingChallenge(false);
        }
      }
    };
    fetchChallenge();
  }, [urlChallengeId, currentUser]);

  useEffect(() => {
    if (urlTab === 'friends' && urlFriendId) {
      navigate(`/profile/${urlFriendId}`, { replace: true });
    }
  }, [urlTab, urlFriendId, navigate]);

  useEffect(() => {
    if (location.state) {
      if (location.state.provokeUserId) {
        handleProvoke(location.state.provokeUserId);
      }
      if (location.state.challengeUserId) {
        setChallengeModalUserId(location.state.challengeUserId);
      }
      if (location.state.activeTab) {
        handleTabChange(location.state.activeTab);
      }
      // Clear the state so it doesn't reopen if the user refreshes
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleSendRequest = async (userId: string) => {
    if (!currentUser) return;
    setLoadingActionId(userId);
    try {
      await sendFriendRequest(currentUser.uid, userId);
      showToast('Friend request sent!');
      setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, friendStatus: 'sent' } : u));
    } finally {
      setLoadingActionId(null);
    }
  };

  const handleAcceptRequest = async (userId: string) => {
    if (!currentUser) return;
    setLoadingActionId(userId);
    try {
      await acceptFriendRequest(currentUser.uid, userId);
      showToast('Friend request accepted!');
      setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, friendStatus: 'friends' } : u));
    } finally {
      setLoadingActionId(null);
    }
  };

  const handleRejectRequest = async (userId: string) => {
    if (!currentUser) return;
    setLoadingActionId(userId);
    try {
      await rejectFriendRequest(currentUser.uid, userId);
      showToast('Friend request ignored.');
      setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, friendStatus: 'none' } : u));
    } finally {
      setLoadingActionId(null);
    }
  };

  const handleCancelRequest = async (userId: string) => {
    if (!currentUser) return;
    setLoadingActionId(userId);
    try {
      await cancelFriendRequest(currentUser.uid, userId);
      showToast('Friend request cancelled.');
      setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, friendStatus: 'none' } : u));
    } finally {
      setLoadingActionId(null);
    }
  };

  const handleRemoveFriend = async (userId: string) => {
    if (!currentUser) return;
    if (window.confirm("Are you sure you want to remove this friend?")) {
      setLoadingActionId(userId);
      try {
        await removeFriend(currentUser.uid, userId);
        showToast('Friend removed.');
        setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, friendStatus: 'none' } : u));
      } finally {
        setLoadingActionId(null);
      }
    }
  };

  const confirmChallenge = async (type: ChallengeType) => {
    if (!currentUser || (!challengeModalUserId && !isBulkAction)) return;
    setIsProcessingChallenge(type);
    try {
      if (isBulkAction) {
        await Promise.all(friendsWithoutStudy.map(friend => createChallenge(currentUser.uid, friend.id, type)));
      } else if (challengeModalUserId) {
        await createChallenge(currentUser.uid, challengeModalUserId, type);
      }
      setChallengeSuccess(type);
      setTimeout(() => {
        setChallengeModalUserId(null);
        setIsBulkAction(false);
        handleTabChange('challenges');
        setIsProcessingChallenge(null);
        setChallengeSuccess(null);
      }, 1500);
    } catch (error) {
      console.error('Failed to create challenge', error);
      setIsProcessingChallenge(null);
    }
  };

  const getProvokeContextMessage = (friendUser: SocialUser | undefined, friendStats: any) => {
    const friendName = friendUser?.username || 'a friend';
    const myName = currentUser?.displayName || 'Someone';
    
    // Check if there's a recently completed challenge between them
    const recentChallenge = completedChallenges.find(c => {
      const isBetweenThem = (c.challengerId === currentUser?.uid && c.challengedId === friendUser?.id) ||
                            (c.challengedId === currentUser?.uid && c.challengerId === friendUser?.id);
      return isBetweenThem;
    });
    
    if (recentChallenge) {
      const iWon = recentChallenge.winnerId === currentUser?.uid;
      const typeLabel = recentChallenge.challengeType === 'crime_game' ? 'Crime Game' :
                        recentChallenge.challengeType === 'daily_training' ? 'Training' :
                        recentChallenge.challengeType === 'word_of_day' ? 'Word of the Day' : 'Points Race';
      if (iWon) {
        return `${myName} poking ${friendName} after winning the ${typeLabel} challenge`;
      }
    }
    
    // Check days without studying
    const daysWithout = friendUser?.daysWithoutStudy || friendStats?.daysWithoutStudy || 0;
    if (daysWithout > 0) {
      return `${myName} poking ${friendName} because ${friendName.split(' ')[0]} is ${daysWithout} ${daysWithout === 1 ? 'day' : 'days'} without studying`;
    }
    
    // Check active challenge
    const activeChallenge = activeChallenges.find(c => {
      return (c.challengerId === currentUser?.uid && c.challengedId === friendUser?.id) ||
             (c.challengedId === currentUser?.uid && c.challengerId === friendUser?.id);
    });
    if (activeChallenge) {
      const typeLabel = activeChallenge.challengeType === 'crime_game' ? 'Crime Game' :
                        activeChallenge.challengeType === 'daily_training' ? 'Training' :
                        activeChallenge.challengeType === 'word_of_day' ? 'Word of the Day' : 'Points Race';
      return `${myName} poking ${friendName} during their ${typeLabel} challenge`;
    }
    
    return `${myName} poking ${friendName}`;
  };

  const handleProvoke = async (userId: string) => {
    if (userId === 'bulk') {
      setIsBulkProvoke(true);
      setProvokeModalUserId('bulk');
    } else {
      setIsBulkProvoke(false);
      setProvokeModalUserId(userId);
    }
    setProvokeMessage('');
    setIsTypingProvoke(false);
    setProvokeValidation(null);
    setSelectedFriendStats(null);
    setProvokeSuccess(false);
    setProvokeContext('');
    
    if (userId !== 'bulk') {
      try {
        const stats = await getUserProfileStats(userId);
        setSelectedFriendStats(stats);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleCheckProvoke = async () => {
    if (!provokeMessage.trim()) return;
    setIsCheckingProvoke(true);
    try {
      const result = await validateGrammar(provokeMessage);
      setProvokeValidation(result);
    } catch (e) {
      console.error('Failed to validate grammar:', e);
    } finally {
      setIsCheckingProvoke(false);
    }
  };

  const handleSendCustomProvoke = async () => {
    if (!currentUser || !provokeModalUserId) return;
    
    // First, check grammar if it hasn't been checked and correct
    if (!provokeValidation?.isCorrect) {
       await handleCheckProvoke();
       return;
    }
    
    try {
      if (isBulkProvoke) {
        await Promise.all(friendsWithoutStudy.map(friend => sendProvocation(currentUser.uid, friend.id, provokeMessage)));
        setProvokeContext(`bulk provoked ${friendsWithoutStudy.length} friends`);
      } else {
        await sendProvocation(currentUser.uid, provokeModalUserId, provokeMessage);
        const friend = friends.find(f => f.id === provokeModalUserId);
        setProvokeContext(getProvokeContextMessage(friend, selectedFriendStats));
      }
      setProvokeSuccess(true);
    } catch (e) {
      console.error('Failed to send provocation:', e);
    }
  };
  
  const handleSendPresetProvoke = async (msg: string) => {
    if (!currentUser || !provokeModalUserId) return;
    try {
      if (isBulkProvoke) {
        await Promise.all(friendsWithoutStudy.map(friend => sendProvocation(currentUser.uid, friend.id, msg)));
        setProvokeMessage(msg);
        setProvokeContext(`bulk provoked ${friendsWithoutStudy.length} friends`);
      } else {
        await sendProvocation(currentUser.uid, provokeModalUserId, msg);
        setProvokeMessage(msg);
        const friend = friends.find(f => f.id === provokeModalUserId);
        setProvokeContext(getProvokeContextMessage(friend, selectedFriendStats));
      }
      setProvokeSuccess(true);
    } catch (e) {
      console.error('Failed to send preset provocation:', e);
    }
  };

  const handleProcessFriendsWithoutStudyImage = async (action: 'share' | 'download' = 'share') => {
    if (!friendsWithoutStudyShareRef.current) return;
    
    setTimeout(async () => {
      try {
        const imgs = friendsWithoutStudyShareRef.current!.querySelectorAll('img');
        imgs.forEach((img) => { 
          img.crossOrigin = 'anonymous'; 
        });

        const canvas = await html2canvas(friendsWithoutStudyShareRef.current!, { 
          useCORS: true,
          scale: 1, // Using scale 1 since the card is already 1080x1920
          backgroundColor: null,
          logging: false
        });
        
        if (action === 'download') {
          const image = canvas.toDataURL("image/png", 1.0);
          const link = document.createElement('a');
          link.href = image;
          link.download = `friends-without-study.png`;
          link.click();
        } else {
          canvas.toBlob(async (blob) => {
            if (!blob) return;
            const file = new File([blob], `friends-without-study.png`, { type: 'image/png' });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              try {
                await navigator.share({
                  files: [file]
                });
              } catch (err) {
                console.log('Share cancelled or failed', err);
              }
            } else {
               const image = canvas.toDataURL("image/png");
               const link = document.createElement('a');
               link.href = image;
               link.download = `friends-without-study.png`;
               link.click();
            }
          }, 'image/png', 1.0);
        }
      } catch (e) {
        console.error("Failed to generate image", e);
      }
    }, 100);
  };

  const handleExportFriendsWithoutStudy = () => handleProcessFriendsWithoutStudyImage('share');
  const handleDownloadFriendsWithoutStudy = () => handleProcessFriendsWithoutStudyImage('download');

  const handleShareProvocation = async () => {
    if (isBulkProvoke) {
      handleProcessFriendsWithoutStudyImage('share');
      return;
    }

    if (!provokeShareRef.current) return;
    
    setTimeout(async () => {
      try {
        const canvas = await html2canvas(provokeShareRef.current!, { 
          useCORS: true,
          scale: 3, 
          backgroundColor: null,
          logging: false
        });
        
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const file = new File([blob], `provocation.png`, { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                files: [file]
              });
            } catch (err) {
              console.log('Share cancelled or failed', err);
            }
          } else {
             const image = canvas.toDataURL("image/png");
             const link = document.createElement('a');
             link.href = image;
             link.download = `provocation.png`;
             link.click();
          }
        }, 'image/png', 1.0);
      } catch (e) {
        console.error("Failed to generate image", e);
      }
    }, 100);
  };

  const handleDownloadProvocation = async () => {
    if (isBulkProvoke) {
      handleProcessFriendsWithoutStudyImage('download');
      return;
    }

    if (!provokeShareRef.current) return;
    
    setTimeout(async () => {
      try {
        const canvas = await html2canvas(provokeShareRef.current!, { 
          useCORS: true,
          scale: 3, 
          backgroundColor: null,
          logging: false
        });
        
        const image = canvas.toDataURL("image/png", 1.0);
        const link = document.createElement('a');
        link.href = image;
        link.download = `provocation.png`;
        link.click();
      } catch (e) {
        console.error("Failed to generate image", e);
      }
    }, 100);
  };

  const handleProcessChallengeImage = async (challenge: ChallengeData, action: 'share' | 'download' = 'share') => {
    setSharingChallenge(challenge);
    
    // Give React time to render the updated state in the hidden div
    setTimeout(async () => {
      if (!shareCardRef.current) return;
      
      try {
        const imgs = shareCardRef.current.querySelectorAll('img');
        imgs.forEach((img) => { 
          img.crossOrigin = 'anonymous'; 
        });

        const canvas = await html2canvas(shareCardRef.current, { 
          useCORS: true,
          scale: 2, 
          backgroundColor: null 
        });
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          
          if (action === 'share') {
            const file = new File([blob], `challenge-${challenge.id}.png`, { type: 'image/png' });
            
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              try {
                const myName = currentUser?.uid === challenge.challengerId ? challenge.challengerName : challenge.challengedName;
                await navigator.share({
                  title: 'Challenge',
                  text: `Check out this challenge on LinguaLearn! Add me as a friend: ${myName || ''}\nhttps://lingualearn.web.app/`,
                  files: [file]
                });
                setSharingChallenge(null);
                return;
              } catch (err) {
                console.log('Share cancelled or failed', err);
              }
            }
          }
          
          // Download (explicitly requested or fallback if share fails/unsupported)
          const image = canvas.toDataURL("image/png");
          const link = document.createElement('a');
          link.href = image;
          link.download = `challenge-${challenge.id}.png`;
          link.click();
          setSharingChallenge(null);
        });
      } catch (e) {
        console.error("Failed to generate image", e);
        setSharingChallenge(null);
      }
    }, 100);
  };

  if (!currentUser) return null;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 pb-20 md:pb-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center gap-2">
            <Users className="text-indigo-600" /> Friends & Challenges
          </h1>
          <p className="text-gray-500 mt-1">Connect, provoke, and compete!</p>
        </div>
      </div>
      
      {/* Friends without study card */}
      {friendsWithoutStudy.length > 0 && (
        <div className="mb-6 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mt-4">
          <div className="p-5 bg-white">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Friends without study:</h2>
            <div className="flex flex-col">
              {friendsWithoutStudy.map((user, index) => (
                <div key={user.id} className={`flex items-center gap-4 py-4 ${index !== friendsWithoutStudy.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  {user.profilePhotoUrl ? (
                    <img referrerPolicy="no-referrer" src={user.profilePhotoUrl} className="w-14 h-14 rounded-full object-cover shadow-sm border border-gray-100" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 font-bold text-xl shadow-sm">
                      {user.username?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-gray-900 text-lg">{user.username}</p>
                    <p className="text-sm font-medium text-red-500 flex items-center gap-1">
                      🚨 {user.daysWithoutStudy} days without study
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-gray-50 p-4 border-t border-gray-100 flex gap-3 flex-wrap">
            <button
              onClick={() => {
                setIsBulkAction(true);
                setChallengeModalUserId('bulk');
              }}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-100 text-indigo-700 font-semibold hover:bg-indigo-200 transition-colors"
            >
              <Swords className="w-4 h-4" /> Bulk Challenge
            </button>
            <button
              onClick={() => handleProvoke('bulk')}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-100 text-red-700 font-semibold hover:bg-red-200 transition-colors"
            >
              <AlertCircle className="w-4 h-4" /> Bulk Provoke
            </button>
            <button
              onClick={handleExportFriendsWithoutStudy}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-100 text-blue-700 font-semibold hover:bg-blue-200 transition-colors"
            >
              <Share2 className="w-4 h-4" /> Export Image
            </button>
            <button
              onClick={handleDownloadFriendsWithoutStudy}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-100 text-emerald-700 font-semibold hover:bg-emerald-200 transition-colors"
            >
              <Download className="w-4 h-4" /> Download Image
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-20 right-4 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-xl animate-fade-in-up">
          {toastMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-2 md:space-x-4 mb-6 overflow-x-auto pb-2 scrollbar-hide">
        <button 
          onClick={() => handleTabChange('friends')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'friends' ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-indigo-50 border border-gray-200'}`}
        >
          <Users className="w-4 h-4" /> Friends ({friends.length})
        </button>
        <button 
          onClick={() => handleTabChange('requests')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'requests' ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-indigo-50 border border-gray-200'}`}
        >
          <UserPlus className="w-4 h-4" /> Requests 
          {friendRequests.length > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{friendRequests.length}</span>
          )}
        </button>
        <button 
          onClick={() => handleTabChange('challenges')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'challenges' ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-indigo-50 border border-gray-200'}`}
        >
          <Swords className="w-4 h-4" /> Challenges ({activeChallenges.length})
        </button>
        <button 
          onClick={() => handleTabChange('search')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'search' ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-indigo-50 border border-gray-200'}`}
        >
          <Search className="w-4 h-4" /> Find
        </button>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 md:p-6 min-h-[400px]">
        {activeTab === 'search' && (
          <div>
            <div className="relative mb-6">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search users by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
              />
            </div>

            <div className="space-y-3">
              {isSearching ? (
                <div className="text-center py-8 text-gray-400 animate-pulse">Searching...</div>
              ) : searchResults.length > 0 ? (
                searchResults.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 md:p-4 rounded-xl border border-gray-100 hover:border-indigo-100 hover:shadow-sm transition-all group">
                    <div 
                      className="flex items-center gap-4 cursor-pointer"
                      onClick={() => navigate(`/profile/${user.id}`)}
                    >
                      {user.profilePhotoUrl ? (
                        <img referrerPolicy="no-referrer" src={user.profilePhotoUrl} className="w-12 h-12 rounded-full object-cover shadow-sm" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 font-bold text-xl shadow-sm">
                          {user.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-gray-800">{user.username}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Flame className="w-3 h-3 text-orange-500" /> {user.streak} day streak
                        </p>
                      </div>
                    </div>
                    
                    {user.friendStatus === 'friends' && (
                      <button 
                        onClick={() => handleRemoveFriend(user.id)}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1 transition-colors"
                      >
                        <UserMinus className="w-4 h-4" /> Remove
                      </button>
                    )}
                    {user.friendStatus === 'sent' && (
                      <button 
                        onClick={() => handleCancelRequest(user.id)}
                        disabled={loadingActionId === user.id}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-100 text-sm font-medium transition-colors disabled:opacity-50 flex justify-center items-center gap-1"
                      >
                        {loadingActionId === user.id ? (
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Clock className="w-4 h-4" /> Cancel Request
                          </>
                        )}
                      </button>
                    )}
                    {user.friendStatus === 'received' && (
                      <button 
                        onClick={() => handleAcceptRequest(user.id)}
                        className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600 flex items-center gap-1 shadow-sm transition-colors"
                      >
                        <UserCheck className="w-4 h-4" /> Accept
                      </button>
                    )}
                    {user.friendStatus === 'none' && (
                      <button 
                        onClick={() => handleSendRequest(user.id)}
                        disabled={loadingActionId === user.id}
                        className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 flex items-center gap-1 shadow-sm transition-colors disabled:bg-indigo-400"
                      >
                        {loadingActionId === user.id ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <UserPlus className="w-4 h-4" /> 
                        )}
                        {loadingActionId === user.id ? 'Adding...' : 'Add'}
                      </button>
                    )}
                  </div>
                ))
              ) : searchQuery.length >= 2 ? (
                <div className="text-center py-8 text-gray-500">No users found.</div>
              ) : (
                <div className="text-center py-8 text-gray-400 flex flex-col items-center">
                  <Search className="w-12 h-12 mb-3 text-gray-200" />
                  Type at least 2 characters to search
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="space-y-8">
            {friendRequests.length === 0 && sentFriendRequests.length === 0 ? (
               <div className="text-center py-12 flex flex-col items-center">
                 <UserCheck className="w-16 h-16 text-gray-200 mb-4" />
                 <h3 className="text-xl font-bold text-gray-700 mb-2">No pending requests</h3>
                 <p className="text-gray-500">You're all caught up!</p>
               </div>
            ) : (
              <>
                {friendRequests.length > 0 && (
                  <div>
                    <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2"><UserPlus className="w-5 h-5 text-indigo-500" /> Received Requests</h3>
                    <div className="space-y-3">
                      {friendRequests.map(user => (
                         <div key={user.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50">
                           <div 
                            className="flex items-center gap-4 cursor-pointer"
                            onClick={() => navigate(`/profile/${user.id}`)}
                           >
                             {user.profilePhotoUrl ? (
                               <img referrerPolicy="no-referrer" src={user.profilePhotoUrl} className="w-12 h-12 rounded-full object-cover shadow-sm" />
                             ) : (
                               <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 font-bold text-xl shadow-sm">
                                 {user.username?.charAt(0).toUpperCase() || 'U'}
                               </div>
                             )}
                             <div>
                               <p className="font-semibold text-gray-800">{user.username}</p>
                               <p className="text-xs text-gray-500">Wants to be friends</p>
                             </div>
                           </div>
                           <div className="flex gap-2">
                              <button 
                                onClick={() => handleRejectRequest(user.id)}
                                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 font-medium transition-colors"
                              >
                                Ignore
                              </button>
                              <button 
                                onClick={() => handleAcceptRequest(user.id)}
                                disabled={loadingActionId === user.id}
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-medium shadow-sm transition-colors disabled:bg-indigo-400 min-w-[90px] flex justify-center items-center gap-2"
                              >
                                {loadingActionId === user.id ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span>Wait...</span>
                                  </>
                                ) : (
                                  'Accept'
                                )}
                              </button>
                           </div>
                         </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {sentFriendRequests.length > 0 && (
                  <div>
                    <h3 className="font-bold text-gray-700 mb-4 mt-6 flex items-center gap-2"><Clock className="w-5 h-5 text-gray-400" /> Sent Requests</h3>
                    <div className="space-y-3">
                      {sentFriendRequests.map(user => (
                         <div key={user.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-white hover:border-gray-200 transition-colors">
                           <div 
                            className="flex items-center gap-4 cursor-pointer"
                            onClick={() => navigate(`/profile/${user.id}`)}
                           >
                             {user.profilePhotoUrl ? (
                               <img referrerPolicy="no-referrer" src={user.profilePhotoUrl} className="w-12 h-12 rounded-full object-cover shadow-sm" />
                             ) : (
                               <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-gray-600 font-bold text-xl shadow-sm">
                                 {user.username?.charAt(0).toUpperCase() || 'U'}
                               </div>
                             )}
                             <div>
                               <p className="font-semibold text-gray-800">{user.username}</p>
                               <p className="text-xs text-gray-500">Awaiting response</p>
                             </div>
                           </div>
                           <button 
                             onClick={() => handleCancelRequest(user.id)}
                             disabled={loadingActionId === user.id}
                             className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-100 font-medium transition-colors disabled:opacity-50 min-w-[90px] flex justify-center items-center gap-2"
                           >
                             {loadingActionId === user.id ? (
                               <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                             ) : (
                               'Cancel'
                             )}
                           </button>
                         </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'friends' && (
          <div>
            {pendingInvites.length > 0 && (
              <div className="mb-6 space-y-2.5">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.sessionId}
                    className="p-4 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex flex-wrap items-center justify-between gap-3 shadow-md animate-in slide-in-from-top duration-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/20 text-white font-bold flex items-center justify-center overflow-hidden">
                        {invite.hostPhoto ? (
                          <img src={invite.hostPhoto} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <BookOpen className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-sm">{invite.hostName} invited you to a Learn Session! 📖</p>
                        <p className="text-xs text-purple-100">Study words & expressions together in real time</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => declineFriendSession(invite.sessionId)}
                        className="px-3.5 py-1.5 rounded-lg bg-black/20 hover:bg-black/30 text-xs font-semibold transition-colors"
                      >
                        Decline
                      </button>
                      <button
                        onClick={async () => {
                          if (currentUser) {
                            await acceptFriendSession(invite.sessionId, {
                              id: currentUser.uid,
                              name: currentUser.displayName || currentUser.email?.split('@')[0] || 'Friend',
                              photo: currentUser.photoURL || undefined,
                            });
                            navigate(`/session/${invite.sessionId}`);
                          }
                        }}
                        className="px-4 py-1.5 rounded-lg bg-white text-purple-700 hover:bg-purple-50 text-xs font-bold shadow-xs transition-colors"
                      >
                        Join Session
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {friends.length === 0 ? (
              <div className="text-center py-12 flex flex-col items-center">
                <Users className="w-16 h-16 text-gray-200 mb-4" />
                <h3 className="text-xl font-bold text-gray-700 mb-2">You don't have any friends yet!</h3>
                <p className="text-gray-500 max-w-md mb-6">Find your friends, follow their progress, and challenge them to improve together.</p>
                <button 
                  onClick={() => handleTabChange('search')}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium shadow-md hover:bg-indigo-700 transition-colors"
                >
                  Find Friends
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {friends.map(user => (
                  <div key={user.id} className="p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow bg-white flex flex-col justify-between h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div 
                        className="flex items-center gap-3 cursor-pointer flex-1"
                        onClick={() => navigate(`/profile/${user.id}`)}
                      >
                        {user.profilePhotoUrl ? (
                          <img referrerPolicy="no-referrer" src={user.profilePhotoUrl} className="w-12 h-12 rounded-full object-cover shadow-sm border border-gray-100" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 font-bold text-xl shadow-sm">
                            {user.username?.charAt(0).toUpperCase() || 'U'}
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-gray-800 hover:text-indigo-600 transition-colors">{user.username}</p>
                            {(() => {
                              const days = user.daysWithoutStudy === undefined ? 0 : user.daysWithoutStudy;
                              const streak = user.streak || 0;
                              const isNotStudying = days > 1 || (days === 1 && streak === 0);
                              
                              if (days === -1) {
                                return <span className="text-xl inline-block animate-bounce" title="New Learner!">🚀</span>;
                              }
                              
                              if (isNotStudying) {
                                if (days <= 2) return <span className="text-xl inline-block animate-pulse" title="Slipping away...">🥺</span>;
                                if (days <= 5) return <span className="text-xl inline-block animate-bounce" style={{ animationDuration: '2s' }} title="We miss you!">😢</span>;
                                return <span className="text-xl inline-block animate-pulse" title="Come back!">😭</span>;
                              }
                              
                              // Studying!
                              if (streak > 5) return <span className="text-xl inline-block animate-pulse" title="On fire!">🔥</span>;
                              return <span className="text-xl inline-block animate-bounce" style={{ animationDuration: '2s' }} title="Doing great!">😎</span>;
                            })()}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {user.daysWithoutStudy && (user.daysWithoutStudy > 1 || (user.daysWithoutStudy === 1 && (!user.streak || user.streak === 0))) ? (
                              <div className="flex items-center gap-1 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full w-fit">
                                <Clock className="w-3.5 h-3.5 text-red-500" /> 
                                <span className="text-xs font-medium text-red-600">{user.daysWithoutStudy} days without studying</span>
                              </div>
                            ) : user.daysWithoutStudy === -1 ? (
                              <div className="flex items-center gap-1 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full w-fit">
                                <Clock className="w-3.5 h-3.5 text-blue-500" /> 
                                <span className="text-xs font-medium text-blue-600">New Learner</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full w-fit">
                                <CheckCircle className="w-3.5 h-3.5 text-green-500" /> 
                                <span className="text-xs font-medium text-green-600">Active learner</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleRemoveFriend(user.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                        title="Remove Friend"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mt-auto">
                      <button 
                        onClick={() => setChallengeModalUserId(user.id)}
                        className="flex items-center justify-center gap-2 py-2 rounded-lg bg-indigo-50 text-indigo-700 font-medium hover:bg-indigo-100 transition-colors text-sm"
                      >
                        <Swords className="w-4 h-4" /> Challenge
                      </button>
                      
                      <button 
                        onClick={() => handleProvoke(user.id)}
                        className="flex items-center justify-center gap-1 py-2 rounded-lg bg-red-50 text-red-700 font-medium hover:bg-red-100 transition-colors text-sm"
                      >
                        <AlertCircle className="w-4 h-4" /> Provoke
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <button 
                        onClick={() => handleStartSession(user)}
                        disabled={loadingActionId === `session_${user.id}`}
                        className="py-2 px-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-xs hover:from-purple-700 hover:to-indigo-700 flex items-center justify-center gap-1.5 shadow-xs transition-all disabled:opacity-50"
                      >
                        {loadingActionId === `session_${user.id}` ? (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <BookOpen className="w-3.5 h-3.5" />
                        )}
                        <span>Study Session</span>
                      </button>

                      <button 
                        onClick={() => handleStartStopGame(user)}
                        disabled={loadingActionId === `stop_${user.id}`}
                        className="py-2 px-2.5 rounded-lg bg-gradient-to-r from-rose-500 to-red-600 text-white font-semibold text-xs hover:from-rose-600 hover:to-red-700 flex items-center justify-center gap-1.5 shadow-xs transition-all disabled:opacity-50"
                      >
                        {loadingActionId === `stop_${user.id}` ? (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <StopCircle className="w-3.5 h-3.5" />
                        )}
                        <span>STOP! Game</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(activeChallenges.length > 0 || completedChallenges.length > 0) && (
              <div className="mt-8">
                <ChallengeSummaryList 
                  activeChallenges={activeChallenges} 
                  completedChallenges={completedChallenges} 
                  currentUser={currentUser} 
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'challenges' && (
          <div className="space-y-8">
            {/* Active Challenges */}
            <div>
              <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Target className="text-indigo-500" /> Active Challenges
                </h2>
                <button
                  onClick={() => handleTabChange('friends')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2"
                >
                  <Swords className="w-4 h-4" /> New Challenge
                </button>
              </div>
              {activeChallenges.length === 0 ? (
                <p className="text-gray-500 italic text-sm">No active challenges. Challenge a friend to a 24-hour race!</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeChallenges.map(challenge => {
                    const isChallenger = currentUser.uid === challenge.challengerId;
                    const myScore = Math.round(isChallenger ? challenge.challengerScore : challenge.challengedScore);
                    const theirScore = Math.round(isChallenger ? challenge.challengedScore : challenge.challengerScore);
                    const theirName = isChallenger ? challenge.challengedName : challenge.challengerName;
                    const theirPhoto = isChallenger ? challenge.challengedPhoto : challenge.challengerPhoto;
                    
                    const timeLeftMs = challenge.endTime - Date.now();
                    const hoursLeft = Math.floor(timeLeftMs / (1000 * 60 * 60));
                    const minsLeft = Math.floor((timeLeftMs % (1000 * 60 * 60)) / (1000 * 60));
                    
                    const isWinning = myScore > theirScore;
                    const isLosing = myScore < theirScore;
                    
                    const typeLabel = challenge.challengeType === 'crime_game' ? 'Crime Game' :
                                      challenge.challengeType === 'daily_training' ? 'Training' :
                                      challenge.challengeType === 'word_of_day' ? 'Word of Day' : 'Point Race';
                    
                    const myAvatarUrl = currentUser.photoURL || getAvatarUrl(currentUser.uid);
                    const theirAvatarUrl = theirPhoto || getAvatarUrl(theirName || 'opponent');

                    return (
                      <div 
                        key={challenge.id} 
                        onClick={() => setViewingChallenge(challenge)}
                        className="relative overflow-hidden rounded-2xl border-2 border-indigo-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer hover:border-indigo-300"
                      >
                        <div className="absolute bottom-0 left-0 h-1 bg-gray-100 w-full">
                          <div 
                            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500" 
                            style={{ width: `${Math.max(5, (timeLeftMs / (24 * 60 * 60 * 1000)) * 100)}%` }}
                          />
                        </div>
                        
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-xs font-bold uppercase tracking-wider text-indigo-500 bg-indigo-50 px-2 py-1 rounded-md">
                            24h {typeLabel}
                          </span>
                          <span className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                            ⏱ {hoursLeft}h {minsLeft}m left
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex flex-col items-center flex-1">
                            <img referrerPolicy="no-referrer" src={myAvatarUrl} className={`w-14 h-14 rounded-full object-cover shadow-sm border-2 ${isWinning ? 'border-green-500' : 'border-gray-200'}`} alt="You" />
                            <span className="font-bold text-2xl mt-2 text-gray-800">{myScore}</span>
                            <span className="text-xs text-gray-500 truncate w-full text-center mt-1">You</span>
                          </div>
                          
                          <div className="flex flex-col items-center px-4">
                            <Swords className="w-8 h-8 text-gray-300 mb-2" />
                            {isWinning && <span className="text-xs font-bold text-green-500">Winning!</span>}
                            {isLosing && <span className="text-xs font-bold text-red-500">Trailing!</span>}
                          </div>
                          
                          <div className="flex flex-col items-center flex-1">
                            <img referrerPolicy="no-referrer" src={theirAvatarUrl} className={`w-14 h-14 rounded-full object-cover shadow-sm border-2 ${isLosing ? 'border-green-500' : 'border-gray-200'}`} alt={theirName || 'Opponent'} />
                            <span className="font-bold text-2xl mt-2 text-gray-800">{theirScore}</span>
                            <span className="text-xs text-gray-500 truncate w-full text-center mt-1">{theirName}</span>
                          </div>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (challenge.challengeType === 'crime_game') navigate('/crime-game');
                              else if (challenge.challengeType === 'daily_training') navigate('/training');
                              else if (challenge.challengeType === 'word_of_day') navigate('/day');
                              else navigate('/dashboard');
                            }}
                            className="flex-1 py-2 bg-indigo-50 text-indigo-600 font-semibold rounded-lg hover:bg-indigo-100 transition-colors"
                          >
                            Go to Challenge Activity
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProcessChallengeImage(challenge, 'download');
                            }}
                            className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors flex items-center justify-center"
                            title="Download Image"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProcessChallengeImage(challenge, 'share');
                            }}
                            className="p-2 bg-pink-50 text-pink-600 hover:bg-pink-100 rounded-lg transition-colors flex items-center justify-center"
                            title="Share Challenge"
                          >
                            <Share2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Completed Challenges */}
            <div>
              <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Trophy className="text-yellow-500" /> Challenge History
                </h2>
                <div className="flex gap-2">
                   <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-bold rounded-lg shadow-sm flex items-center gap-1.5">
                     <Trophy className="w-3.5 h-3.5" />
                     {completedChallenges.filter(c => c.winnerId === currentUser.uid).length} Victories
                   </span>
                   <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-bold rounded-lg shadow-sm flex items-center gap-1.5">
                     <Frown className="w-3.5 h-3.5" />
                     {completedChallenges.filter(c => c.winnerId && c.winnerId !== currentUser.uid).length} Defeats
                   </span>
                </div>
              </div>
              {completedChallenges.length === 0 ? (
                <p className="text-gray-500 italic text-sm">No completed challenges yet.</p>
              ) : (
                <div className="space-y-4">
                  {completedChallenges.map(challenge => {
                    const isChallenger = currentUser.uid === challenge.challengerId;
                    const myScore = Math.round(isChallenger ? challenge.challengerScore : challenge.challengedScore);
                    const theirScore = Math.round(isChallenger ? challenge.challengedScore : challenge.challengerScore);
                    const theirName = isChallenger ? challenge.challengedName : challenge.challengerName;
                    const iWon = challenge.winnerId === currentUser.uid;
                    const isDraw = !challenge.winnerId;
                    const typeLabel = challenge.challengeType === 'crime_game' ? 'Crime Game' :
                                      challenge.challengeType === 'daily_training' ? 'Training' :
                                      challenge.challengeType === 'word_of_day' ? 'Word of Day' : 'Point Race';
                    
                    return (
                      <div 
                        key={challenge.id} 
                        onClick={() => setViewingChallenge(challenge)}
                        className="bg-gray-50 rounded-xl p-4 border border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${iWon ? 'bg-green-100 text-green-600' : isDraw ? 'bg-gray-200 text-gray-600' : 'bg-red-100 text-red-600'}`}>
                            {iWon ? <Trophy className="w-6 h-6" /> : isDraw ? <Swords className="w-6 h-6" /> : <Frown className="w-6 h-6" />}
                          </div>
                          <div>
                            <p className="font-bold text-gray-800">You vs {theirName} <span className="text-xs font-normal text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded ml-2">{typeLabel}</span></p>
                            <p className="text-sm font-medium">
                              {iWon ? <span className="text-green-600">Victory!</span> : isDraw ? <span className="text-gray-600">Draw</span> : <span className="text-red-600">Defeat</span>}
                              <span className="text-gray-500 mx-2">|</span>
                              Score: {myScore} - {theirScore}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProcessChallengeImage(challenge, 'download');
                            }}
                            className="p-2 text-blue-500 hover:bg-blue-50 rounded-full transition-colors"
                            title="Download Image"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProcessChallengeImage(challenge, 'share');
                            }}
                            className="p-2 text-pink-500 hover:bg-pink-50 rounded-full transition-colors"
                            title="Share Challenge"
                          >
                            <Share2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ChallengeShareCard challenge={sharingChallenge} currentUser={currentUser} cardRef={shareCardRef} />
      
      <FriendsWithoutStudyShareCard friendsWithoutStudy={friendsWithoutStudy} cardRef={friendsWithoutStudyShareRef} />
      
      <ProvokeShareCard 
        currentUser={currentUser} 
        friend={friends.find(f => f.id === provokeModalUserId)} 
        provokeMessage={provokeMessage} 
        provokeContext={provokeContext} 
        cardRef={provokeShareRef} 
      />
      
      {/* Challenge Type Modal */}
      {challengeModalUserId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl relative">
            <button 
              onClick={() => setChallengeModalUserId(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Swords className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {isBulkAction ? `Challenge ${friendsWithoutStudy.length} Friends` : 'Choose Challenge Type'}
              </h3>
              <p className="text-gray-500 text-sm">Select what you want to compete in for the next 24 hours!</p>
            </div>
            
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1 scrollbar-hide">
              <button 
                onClick={() => confirmChallenge('points')}
                disabled={!!isProcessingChallenge}
                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 hover:border-indigo-500 hover:bg-indigo-50 transition-all group shrink-0 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-yellow-500" />
                  <div className="text-left">
                    <p className="font-bold text-gray-800">Total Points Race</p>
                    <p className="text-xs text-gray-500">Who gets the most points overall</p>
                  </div>
                </div>
                {isProcessingChallenge === 'points' && !challengeSuccess ? (
                  <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                ) : challengeSuccess === 'points' ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : null}
              </button>
              
              <button 
                onClick={() => confirmChallenge('daily_training')}
                disabled={!!isProcessingChallenge}
                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 hover:border-blue-500 hover:bg-blue-50 transition-all group disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <Target className="w-6 h-6 text-blue-500" />
                  <div className="text-left">
                    <p className="font-bold text-gray-800">Daily Training</p>
                    <p className="text-xs text-gray-500">Who does the most training sessions</p>
                  </div>
                </div>
                {isProcessingChallenge === 'daily_training' && !challengeSuccess ? (
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                ) : challengeSuccess === 'daily_training' ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : null}
              </button>
              
              <button 
                onClick={() => confirmChallenge('crime_game')}
                disabled={!!isProcessingChallenge}
                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 hover:border-purple-500 hover:bg-purple-50 transition-all group disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-purple-500" />
                  <div className="text-left">
                    <p className="font-bold text-gray-800">Crime Game</p>
                    <p className="text-xs text-gray-500">Who solves the most crimes</p>
                  </div>
                </div>
                {isProcessingChallenge === 'crime_game' && !challengeSuccess ? (
                  <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                ) : challengeSuccess === 'crime_game' ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : null}
              </button>
              
              <button 
                onClick={() => confirmChallenge('word_of_day')}
                disabled={!!isProcessingChallenge}
                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 hover:border-amber-500 hover:bg-amber-50 transition-all group disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <UserCheck className="w-6 h-6 text-amber-500" />
                  <div className="text-left">
                    <p className="font-bold text-gray-800">Word of the Day</p>
                    <p className="text-xs text-gray-500">Who completes the most word practices</p>
                  </div>
                </div>
                {isProcessingChallenge === 'word_of_day' && !challengeSuccess ? (
                  <div className="w-5 h-5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                ) : challengeSuccess === 'word_of_day' ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : null}
              </button>
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-100 text-center">
              <button onClick={() => setChallengeModalUserId(null)} className="text-gray-500 hover:text-gray-800 font-medium">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Provoke Modal */}
      {provokeModalUserId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl relative">
            <button 
              onClick={() => setProvokeModalUserId(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            {provokeSuccess ? (
              <div className="text-center py-4">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Provocation Sent!</h3>
                <p className="text-gray-500 mb-8">
                  {isBulkProvoke 
                    ? 'Your friends will get a notification to wake up and study.' 
                    : 'Your friend will get a notification to wake up and study.'}
                </p>
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={handleShareProvocation}
                    className="w-full py-3.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex justify-center items-center gap-2"
                  >
                    <Share2 className="w-5 h-5" /> Share on Social Media
                  </button>
                  <button 
                    onClick={handleDownloadProvocation}
                    className="w-full py-3.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex justify-center items-center gap-2"
                  >
                    <Download className="w-5 h-5" /> Download Image
                  </button>
                  <button 
                    onClick={() => setProvokeModalUserId(null)}
                    className="w-full py-3.5 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Flame className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {isBulkProvoke ? `Provoke ${friendsWithoutStudy.length} Friends` : 'Send Provocation'}
                  </h3>
                  <p className="text-gray-500 text-sm">Choose a message or write your own to wake them up!</p>
                </div>
                
                {!isTypingProvoke ? (
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1 scrollbar-hide">
                    {currentProvokeOptions.map((msg, index) => (
                      <button 
                        key={index}
                        onClick={() => handleSendPresetProvoke(msg)}
                        className="w-full flex items-center p-4 rounded-xl border-2 border-gray-100 hover:border-red-400 hover:bg-red-50 transition-all text-left group"
                      >
                        <span className="font-medium text-gray-800 group-hover:text-red-700">{msg}</span>
                      </button>
                    ))}
                    
                    <button 
                      onClick={() => setIsTypingProvoke(true)}
                      className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all"
                    >
                      <MessageSquare className="w-5 h-5" />
                      <span className="font-medium">Write my own (in English)</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 scrollbar-hide">
                    <textarea
                      value={provokeMessage}
                      onChange={(e) => {
                        setProvokeMessage(e.target.value);
                        setProvokeValidation(null);
                      }}
                      placeholder="Type your provocation here in English..."
                      className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none resize-none h-32"
                    />
                    
                    {provokeValidation && (
                      <div className={`p-4 rounded-xl text-sm ${provokeValidation.isCorrect ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                        <div className="flex items-start gap-2">
                          {provokeValidation.isCorrect ? <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 shrink-0 mt-0.5" />}
                          <div>
                            <p className="font-medium">{provokeValidation.feedback}</p>
                            {provokeValidation.errors && provokeValidation.errors.length > 0 && (
                              <ul className="mt-2 space-y-1 list-disc list-inside opacity-90">
                                {provokeValidation.errors.map((err, i) => (
                                  <li key={i}>{err.message}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setIsTypingProvoke(false)}
                        className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                      >
                        Back
                      </button>
                      <button 
                        onClick={provokeValidation?.isCorrect ? handleSendCustomProvoke : handleCheckProvoke}
                        disabled={isCheckingProvoke || !provokeMessage.trim()}
                        className="flex-[2] px-4 py-3 rounded-xl bg-indigo-600 text-white font-medium shadow-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 flex items-center justify-center gap-2"
                      >
                        {isCheckingProvoke ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : provokeValidation?.isCorrect ? (
                          'Send Provocation'
                        ) : (
                          'Check Grammar'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      
      {viewingChallenge && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-3xl max-w-md w-full p-8 shadow-2xl relative text-white">
            <button 
              onClick={() => {
                setViewingChallenge(null);
                setSearchParams((prev) => {
                  prev.delete('challengeId');
                  return prev;
                });
              }}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-3 mb-6 justify-center">
              {viewingChallenge.winnerId ? <Trophy className="w-8 h-8 text-yellow-400" /> : <Swords className="w-8 h-8 text-yellow-400" />}
              <h1 className="text-2xl font-black tracking-tight">{viewingChallenge.winnerId ? 'Challenge Result!' : 'Active Challenge!'}</h1>
            </div>
            
            <div className="flex items-center justify-center w-full gap-2 bg-white/10 p-4 rounded-2xl backdrop-blur-md border border-white/20 shadow-xl mb-6">
              {/* Challenger */}
              <div className="flex flex-col items-center flex-1">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center text-2xl font-bold border-2 border-white shadow-lg mb-2 overflow-hidden">
                  <img referrerPolicy="no-referrer" src={viewingChallenge.challengerPhoto || getAvatarUrl(viewingChallenge.challengerName || viewingChallenge.challengerId || 'P1')} className="w-full h-full object-cover" />
                </div>
                <h2 className="text-sm font-bold text-white pb-1 truncate w-full text-center max-w-[100px]">{viewingChallenge.challengerName || 'Player 1'}</h2>
                <p className="text-2xl font-black text-white leading-none">{Math.round(viewingChallenge.challengerScore || 0)}</p>
                <p className="text-[10px] font-medium text-indigo-200 uppercase tracking-widest mt-1">Score</p>
              </div>
              
              <div className="flex flex-col items-center justify-center mx-2 w-16">
                <Swords className="w-8 h-8 text-yellow-400 mb-2" />
                {viewingChallenge.winnerId ? (
                  <div className="bg-yellow-400 text-yellow-900 font-bold px-2 py-0.5 rounded-full uppercase text-[10px] shadow-lg whitespace-nowrap">Winner</div>
                ) : viewingChallenge.endTime && viewingChallenge.endTime < Date.now() ? (
                  <div className="bg-gray-300 text-gray-800 font-bold px-2 py-0.5 rounded-full uppercase text-[10px] shadow-lg whitespace-nowrap">Draw</div>
                ) : (
                  <div className="bg-blue-400 text-blue-900 font-bold px-2 py-0.5 rounded-full uppercase text-[10px] shadow-lg whitespace-nowrap">Vs</div>
                )}
              </div>
              
              {/* Challenged */}
              <div className="flex flex-col items-center flex-1">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 flex items-center justify-center text-2xl font-bold border-2 border-white shadow-lg mb-2 overflow-hidden">
                  <img referrerPolicy="no-referrer" src={viewingChallenge.challengedPhoto || getAvatarUrl(viewingChallenge.challengedName || viewingChallenge.challengedId || 'P2')} className="w-full h-full object-cover" />
                </div>
                <h2 className="text-sm font-bold text-white pb-1 truncate w-full text-center max-w-[100px]">{viewingChallenge.challengedName || 'Player 2'}</h2>
                <p className="text-2xl font-black text-white leading-none">{Math.round(viewingChallenge.challengedScore || 0)}</p>
                <p className="text-[10px] font-medium text-purple-200 uppercase tracking-widest mt-1">Score</p>
              </div>
            </div>
            
            <div className="flex flex-col items-center gap-3">
              {currentUser.uid !== viewingChallenge.challengerId && currentUser.uid !== viewingChallenge.challengedId && (
                <button 
                  onClick={() => {
                    setViewingChallenge(null);
                    setSearchParams((prev) => {
                      prev.delete('challengeId');
                      return prev;
                    });
                    setSearchQuery(viewingChallenge.challengerName);
                    handleTabChange('search');
                  }}
                  className="w-full py-3 bg-white text-indigo-900 font-bold rounded-xl shadow-lg hover:bg-indigo-50 transition-colors"
                >
                  Add {viewingChallenge.challengerName} as Friend
                </button>
              )}
              <button 
                onClick={() => {
                  setViewingChallenge(null);
                  setSearchParams((prev) => {
                    prev.delete('challengeId');
                    return prev;
                  });
                }}
                className="w-full py-3 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
